import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { HelperService } from 'src/app/helper.service';
import { ImsService } from '../../ims.service';
import { ToasterService } from 'src/app/toaster.service';
import { Toaster, ToasterType } from 'src/app/shared/types';
import { GuestDetailsService } from '../guest-details.service';
import {
  resolveDomainValidationErrorMessage,
  resolveDomainValidationSuccessMessage,
} from 'src/app/shared/domain-validation/domain-validation-messages';

@Component({
    selector: 'app-guest-account',
    templateUrl: './guest-account.component.html',
    styleUrls: ['./guest-account.component.css']

})
export class GuestAccountComponent implements OnInit, OnChanges {
    @Input() data;
    public info: any;
    public newEmail = '';
    public updatingEmail = false;
    public errorMsg = '';
    public corporateEmailChecked = false;
    public corporateEmailDisabled = false;
    public showEmailDialog = false;
    public isEmailVerificationInProgress = false;
    public domainType = '';
    public domainTypeLookupLoading = false;
    public readonly domainTypeOptions = ['corporate', 'non_corporate'];
    public walletBalance: string | null = null;
    public walletBalanceLoading: boolean = false;
    public walletBalanceError: boolean = false;
    constructor(
        private imsService: ImsService, 
        private toasterService: ToasterService,
        private guestDetailsService: GuestDetailsService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.data && changes.data.currentValue && changes.data.currentValue.dataFetched === false) {
            this.blankCard();
        } else if (changes.data && changes.data.currentValue) {
            this.data = changes.data.currentValue;
            this.constructCard();
            // Set checkbox state based on email only if not in verification process
            if (!this.isEmailVerificationInProgress) {
                const guest = this.data.profiles.find(profile => profile.type === 'guest');
                this.corporateEmailChecked = !!(guest && guest.email);
                this.corporateEmailDisabled = !!(guest && guest.email);
            }
            if (this.data && this.data.dataFetched === true && this.data.profiles) {
                const guestProfile = this.data.profiles.find(profile => profile.type === 'guest');
                if (guestProfile && guestProfile.id) {
                    this.fetchWalletBalance();
                }
            }
        } else {
            this.emptyCard();
        }
    }
    ngOnInit() {}

    blankCard() {
        this.info = {
            header: 'Hopr account',
            dataFetched: false,
            data: []
        };
    }

    emptyCard() {
        this.info = {
            header: 'Hopr account',
            dataFetched: false,
            data: null
        };
    }

    constructCard() {
        if (this.data) {
            const guest = this.data.profiles.find(profile => profile.type === 'guest');
            const cardData = [
                {
                    header: 'Current Status',
                    className: guest.status === "ACTIVE" ? 'green-badge' : 'red-badge',
                    value: guest.status === "ACTIVE" ? 'Active' : 'Inactive',
                    information: !guest.status
                }
            ];

            // Add wallet balance row if we've attempted to fetch it
            const walletBalanceCardData = HelperService.buildWalletBalanceCardData(
                this.walletBalance,
                this.walletBalanceLoading,
                this.walletBalanceError
            );
            if (walletBalanceCardData) {
                cardData.push(walletBalanceCardData as any);
            }

            // Add corporate email checkbox row
            cardData.push({ header: this.corporateEmailLabel, isCheckbox: true } as any);

            // Add email updated by row
            if (guest) {
                cardData.push({
                    header: 'Email Updated By',
                    value: guest.updatedBy || 'N/A'
                } as any);
            }

            this.info = {
                header: 'Hopr account',
                dataFetched: true,
                data: cardData,
                reload: true
            };
        }
    }

    formatDate(date) {
        return HelperService.dateString(date);
    };

    // --- Checkbox handler ---
    onCorporateEmailCheckboxChange(event: any) {
        if (event.target.checked && !this.corporateEmailDisabled) {
            this.isEmailVerificationInProgress = true;
            this.showEmailDialog = true;
            this.newEmail = '';
            this.resetDomainTypeSelection();
            this.errorMsg = '';
        }
    }

    resetDomainTypeSelection() {
        this.domainType = '';
        this.domainTypeLookupLoading = false;
    }

    canLookupEmail(): boolean {
        const trimmedEmail = this.newEmail ? this.newEmail.trim() : '';
        return trimmedEmail.indexOf('@') > 0;
    }

    lookupEmailDomain() {
        const trimmedEmail = this.newEmail ? this.newEmail.trim() : '';
        if (!trimmedEmail || trimmedEmail.indexOf('@') <= 0) {
            this.resetDomainTypeSelection();
            return;
        }

        this.domainTypeLookupLoading = true;
        this.imsService.lookupEmailDomain(trimmedEmail).subscribe({
            next: (response: any) => {
                this.domainTypeLookupLoading = false;
                if (response && response.exists && response.domainType) {
                    this.domainType = response.domainType;
                } else {
                    this.domainType = '';
                }
            },
            error: () => {
                this.domainTypeLookupLoading = false;
                this.domainType = '';
            }
        });
    }

    // --- Dialog close handler ---
    closeEmailDialog() {
        this.showEmailDialog = false;
        this.isEmailVerificationInProgress = false;
        // Uncheck the box when cancelled
        this.corporateEmailChecked = false;
    }

    // --- Submit email from dialog ---
    submitEmail() {
        if (!this.data || !this.data.profiles) return;
        const guestProfile = this.data.profiles.find(profile => profile.type === 'guest');
        if (!guestProfile || !this.newEmail) {
            this.errorMsg = 'Please enter a valid email.';
            return;
        }
        if (!this.domainType) {
            this.errorMsg = 'Please select a domain type.';
            return;
        }
        this.updatingEmail = true;
        this.errorMsg = '';
        this.imsService.updateUserEmail(this.data.id, this.newEmail, ['guest'], this.domainType)
          .subscribe({
            next: (res: any) => {
              this.showEmailDialog = false;
              this.isEmailVerificationInProgress = false;
              this.updatingEmail = false;
              this.errorMsg = '';
              guestProfile.email = this.newEmail;
              this.constructCard();
              this.corporateEmailChecked = true;
              this.corporateEmailDisabled = true;
              // Show a success toaster
              const successCopy = resolveDomainValidationSuccessMessage(res) ||
                'Corporate email verified successfully!';
              this.toasterService.showToaster(new Toaster({
                type: ToasterType.SUCCESS,
                message: successCopy
              }));
              window.location.reload();
            },
            error: (err) => {
              this.updatingEmail = false;
              this.errorMsg = resolveDomainValidationErrorMessage(err);
              // Don't reset isEmailVerificationInProgress on error, let user try again or cancel
            }
          });
    }

    get showVerifyEmailToggle(): boolean {
        if (!this.info || !this.info.data) return false;
        const emailField = this.info.data.find(d => d.header === 'Email');
        return !emailField || !emailField.value || emailField.value === 'N/A';
    }

    get corporateEmailLabel(): string {
        if (this.corporateEmailChecked && this.corporateEmailDisabled) {
            return 'Corporate Email (Verified)';
        }
        return 'Corporate Email (Not Verified)';
    }

    fetchWalletBalance() {
        if (!this.data || !this.data.profiles) {
            return;
        }
        const guestProfile = this.data.profiles.find(profile => profile.type === 'guest');
        if (!guestProfile || !guestProfile.id) {
            return;
        }

        this.walletBalanceLoading = true;
        this.walletBalanceError = false;

        this.guestDetailsService.getGuestWalletBalance(guestProfile.id).subscribe({
            next: (response: any) => {
                this.walletBalanceLoading = false;
                let balance = null;
                if (response && 'balance' in response) {
                    balance = response.balance;
                }

                if (balance !== null && balance !== undefined) {
                    const formattedBalance = parseFloat(balance).toFixed(2);
                    this.walletBalance = formattedBalance;
                    this.walletBalanceError = false;
                } else {
                    this.walletBalance = 'Unable to fetch';
                    this.walletBalanceError = true;
                }
                this.constructCard();
            },
            error: (err) => {
                this.walletBalanceLoading = false;
                this.walletBalanceError = true;
                this.walletBalance = 'Unable to fetch';
                this.constructCard();
            }
        });
    }

    refreshWalletBalance() {
        this.walletBalanceError = false;
        this.fetchWalletBalance();
    }

    onRefresh() {
        this.refreshWalletBalance();
    }
}
